import React from "react"
import { useTranslation } from "react-i18next"
import { Bar } from "react-chartjs-2"
import useTranslatedLabels from "services/charts/translatedLabels"
import { stackedAxes } from "services/charts/axes"
import { getFormattedDatasets } from "services/charts/datasets"
import { DEFAULT_OPTIONS } from "services/charts/default"
import { BOTTOM_LEGEND, HIDDEN_LEGEND } from "services/charts/legend"
import { ChartContainer } from "views/organisms/organization/tables/ChartContainer"
import PropTypes from "prop-types"
import { COLORS } from "utils/constants/colors"
import { DETERMINE_UNITS } from "services/units/units"
import { convertDatasetsToReadable } from "services/units/valueConversion"
import { round } from "services/units/round"
import { truncateLabel } from "services/charts/labelTruncation"

const CumulativeBarChartTemplate = ({
  title,
  datasets,
  labelInDatasetsIsArray,
  labelPath,
  labelKeys,
  unitPath,
  unitKey,
  modifiedLegend,
  firstColumnAlwaysGrey,
  hiddenLegend,
  showLabelAndUnitsOnBar,
  brAfterLabel,
  showLabelOnBar,
  compareBarColors,
  helpText,
  useBackgroundColorsInDatasets,
  fabAndUseCompareBarColors,
  pillar,
  greyIfOnly1Column,
  addOtherCategory,
  sortDatasets,
  compareWithPreviousBarColors,
  shouldTranslateLabels,
}) => {
  const { t } = useTranslation()

  let formattedDatasets = getFormattedDatasets({
    datasets,
    fabAndUseCompareBarColors,
    useBackgroundColorsInDatasets,
    labelKeys,
    compareBarColors,
    firstColumnAlwaysGrey,
    greyIfOnly1Column,
    addOtherCategory,
    compareWithPreviousBarColors,
  })

  const translatedLabels = shouldTranslateLabels
    ? useTranslatedLabels(labelPath, labelKeys)
    : labelKeys

  let sortedAscDataset
  if (!sortDatasets) {
    sortedAscDataset = formattedDatasets
  } else if (formattedDatasets.length === 1) {
    sortedAscDataset = formattedDatasets.map((dataset) => {
      return {
        ...dataset,
        data: dataset.data.sort((a, b) => b - a),
      }
    })
  } else {
    const sortInsideColumn = formattedDatasets.sort(
      (a, b) => b.data[0] - a.data[0]
    )

    const transposedDatasets = sortInsideColumn.reduce(
      (acc, dataset, datasetIndex) => {
        dataset.data.forEach((value, index) => {
          if (!acc[index]) {
            acc[index] = {
              data: [],
              labels: [],
              backgroundColor: dataset.backgroundColor ? [] : undefined,
              originalIndex: index,
            }
          }
          acc[index].data.push(value)
          acc[index].labels.push(dataset.label)
          if (
            dataset.backgroundColor &&
            Array.isArray(dataset.backgroundColor)
          ) {
            acc[index].backgroundColor.push(dataset.backgroundColor[index])
          }
        })
        return acc
      },
      []
    )

    const transposedDatasetsWithSum = transposedDatasets.map((dataset) => ({
      ...dataset,
      sum: dataset.data.reduce((sum, value) => sum + value, 0),
    }))

    transposedDatasetsWithSum.sort((a, b) => b.sum - a.sum)

    const reorderedLabels = transposedDatasetsWithSum.map(
      (dataset) => translatedLabels[dataset.originalIndex]
    )

    translatedLabels.splice(0, translatedLabels.length, ...reorderedLabels)
    sortedAscDataset = formattedDatasets.map((dataset) => {
      const newDataset = {
        ...dataset,
        data: transposedDatasetsWithSum.map((transposed, index) => {
          const labelIndex = transposed.labels.indexOf(dataset.label)
          return labelIndex !== -1 ? transposed.data[labelIndex] : 0
        }),
      }

      if (dataset.backgroundColor && Array.isArray(dataset.backgroundColor)) {
        newDataset.backgroundColor = transposedDatasetsWithSum.map(
          (transposed) => {
            return dataset.backgroundColor[transposed.originalIndex]
          }
        )
      }

      return newDataset
    })
  }

  const {
    datasets: convertedDatasets,
    unit,
  } = convertDatasetsToReadable(
    sortedAscDataset,
    unitKey,
    DETERMINE_UNITS(unitKey),
    (value) => round(value)
  )

  const totalValueOfDatasets = convertedDatasets.map((dataset) =>
    dataset.data.reduce((acc, value) => acc + value, 0)
  )

  unitKey = unit

  const translatedUnit = unitKey === null ? "" : t(`${unitPath}.${unitKey}`)
  const data = {
    labels: translatedLabels,
    datasets: convertedDatasets,
  }

  const options = {
    ...DEFAULT_OPTIONS,
    legend: modifiedLegend
      ? {
          display: true,
          position: "bottom",
          labels: {
            generateLabels: modifiedLegend,
          },
        }
      : BOTTOM_LEGEND,
    scales: stackedAxes(translatedUnit),
    tooltips: {
      callbacks: {
        label: function (tooltipItem, data) {
          const label = labelInDatasetsIsArray
            ? data.datasets[tooltipItem.datasetIndex].label[tooltipItem.index]
            : data.datasets[tooltipItem.datasetIndex].label
          return showLabelOnBar
            ? `${label}: ${
                data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]
              } ${translatedUnit}`
            : `${label}: ${tooltipItem.yLabel} ${translatedUnit}`
        },
      },
    },
    plugins: {
      datalabels: {
        display: function (context) {
          return (
            (context.dataset.data[context.dataIndex] /
              totalValueOfDatasets[0]) *
              100 >
            5
          )
        },
        formatter: function (value, context) {
          if (showLabelOnBar) {
            const label = labelInDatasetsIsArray
              ? context.dataset.label[context.dataIndex]
              : context.dataset.label
            return truncateLabel(label, context)
          } else if (showLabelAndUnitsOnBar) {
            const label = labelInDatasetsIsArray
              ? context.dataset.label[context.dataIndex]
              : context.dataset.label
            const truncatedLabel = truncateLabel(label, context)
            let getUnitPath = unitPath === "" ? "" : translatedUnit
            return `${truncatedLabel}${brAfterLabel ? "\n" : " "}${
              context.dataset.data[context.dataIndex]
            } ${getUnitPath}`
          } else {
            return value
          }
        },
        textAlign: "center",
        color: COLORS.white,
        font: {
          weight: "bold",
        },
        align: showLabelAndUnitsOnBar ? "center" : undefined,
      },
    },
    elements: {
      rectangle: {
        borderWidth: 1,
        borderColor: COLORS.white,
      },
    },
  }

  if (hiddenLegend) {
    options.legend = HIDDEN_LEGEND
  }

  return (
    <ChartContainer title={title} helpText={helpText} pillar={pillar}>
      <Bar data={data} options={options} />
    </ChartContainer>
  )
}

export default CumulativeBarChartTemplate

CumulativeBarChartTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  datasets: PropTypes.array.isRequired,
  labelPath: PropTypes.string,
  labelKeys: PropTypes.array.isRequired,
  unitPath: PropTypes.string.isRequired,
  unitKey: PropTypes.string.isRequired,
  fabAndUseCompareBarColors: PropTypes.bool,
  helpText: PropTypes.string,
  hiddenLegend: PropTypes.bool,
  showLabelAndUnitsOnBar: PropTypes.bool,
  brAfterLabel: PropTypes.bool,
  useBackgroundColorsInDatasets: PropTypes.bool,
  showLabelOnBar: PropTypes.bool,
  compareBarColors: PropTypes.bool,
  firstColumnAlwaysGrey: PropTypes.bool,
  pillar: PropTypes.string,
  modifiedLegend: PropTypes.func,
  labelInDatasetsIsArray: PropTypes.bool,
  greyIfOnly1Column: PropTypes.bool,
  addOtherCategory: PropTypes.bool,
  sortDatasets: PropTypes.bool,
  compareWithPreviousBarColors: PropTypes.bool,
  shouldTranslateLabels: PropTypes.bool,
}

CumulativeBarChartTemplate.defaultProps = {
  labelPath: "",
  showLabelAndUnitsOnBar: false,
  brAfterLabel: false,
  useBackgroundColorsInDatasets: false,
  showLabelOnBar: false,
  compareBarColors: false,
  labelInDatasetsIsArray: false,
  firstColumnAlwaysGrey: false,
  hiddenLegend: false,
  fabAndUseCompareBarColors: false,
  addOtherCategory: false,
  sortDatasets: true,
  compareWithPreviousBarColors: false,
  shouldTranslateLabels: true,
}
